import React from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { useState } from "react";
import { useSwipeable } from "react-swipeable";
import { Dialog } from "@headlessui/react";

interface ImageCarouselModalProps {
  images: string[];
  isOpen: boolean;
  initialIndex: number;
  onClose: () => void;
  alt?: string;
}

const ImageCarouselModal: React.FC<ImageCarouselModalProps> = ({
  images,
  isOpen,
  initialIndex,
  onClose,
  alt = "Gallery image"
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loaded, setLoaded] = useState(false);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (currentIndex < images.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    },
    onSwipedRight: () => {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    },
    trackMouse: true,
  });

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      handlePrevious();
    } else if (event.key === 'ArrowRight') {
      handleNext();
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, currentIndex]);

  React.useEffect(() => {
    setCurrentIndex(initialIndex);
    setLoaded(false);
  }, [initialIndex]);

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
    }),
  };

  const [direction, setDirection] = useState(0);

  const handleImageChange = (newIndex: number) => {
    setDirection(newIndex > currentIndex ? 1 : -1);
    setCurrentIndex(newIndex);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-90" />
      
      <MotionConfig
        transition={{
          x: { type: "spring", stiffness: 300, damping: 30 },
          opacity: { duration: 0.2 },
        }}
      >
        <div
          className="relative z-50 flex aspect-[3/2] w-full max-w-7xl items-center wide:h-full xl:taller-than-854:h-auto"
          {...handlers}
        >
          {/* Main image */}
          <div className="w-full overflow-hidden">
            <div className="relative flex aspect-[3/2] items-center justify-center">
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="absolute"
                >
                  <img
                    src={images[currentIndex] || ""}
                    width={1280}
                    height={853}
                    alt={`${alt} ${currentIndex + 1}`}
                    onLoad={() => setLoaded(true)}
                    sizes="(max-width: 1025px) 100vw, 1280px"
                    className="max-h-[80vh] w-auto object-contain"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation buttons and controls */}
          <div className="absolute inset-0 mx-auto flex max-w-7xl items-center justify-center">
            {loaded && (
              <div className="relative aspect-[3/2] max-h-full w-full">
                {/* Previous/Next buttons */}
                {currentIndex > 0 && (
                  <button
                    className="absolute left-3 top-[calc(50%-16px)] rounded-full bg-black/50 p-3 text-white/75 backdrop-blur-lg transition hover:bg-black/75 hover:text-white focus:outline-none"
                    style={{ transform: "translate3d(0, 0, 0)" }}
                    onClick={handlePrevious}
                  >
                    <ChevronLeftIcon className="h-6 w-6" />
                  </button>
                )}
                {currentIndex + 1 < images.length && (
                  <button
                    className="absolute right-3 top-[calc(50%-16px)] rounded-full bg-black/50 p-3 text-white/75 backdrop-blur-lg transition hover:bg-black/75 hover:text-white focus:outline-none"
                    style={{ transform: "translate3d(0, 0, 0)" }}
                    onClick={handleNext}
                  >
                    <ChevronRightIcon className="h-6 w-6" />
                  </button>
                )}

                {/* Close button */}
                <div className="absolute top-0 right-0 flex items-center gap-2 p-3 text-white">
                  <button
                    onClick={onClose}
                    className="rounded-full bg-black/50 p-2 text-white/75 backdrop-blur-lg transition hover:bg-black/75 hover:text-white"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Bottom thumbnail navigation */}
            <div className="fixed inset-x-0 bottom-0 z-40 overflow-hidden bg-gradient-to-b from-black/0 to-black/60">
              <motion.div
                initial={false}
                className="mx-auto mt-6 mb-6 flex aspect-[3/2] h-14"
              >
                <AnimatePresence initial={false}>
                  {images.map((image, index) => (
                    <motion.button
                      initial={{
                        width: "0%",
                        x: `${Math.max((currentIndex - 1) * -100, 15 * -100)}%`,
                      }}
                      animate={{
                        scale: index === currentIndex ? 1.25 : 1,
                        width: "100%",
                        x: `${Math.max(currentIndex * -100, 15 * -100)}%`,
                      }}
                      exit={{ width: "0%" }}
                      onClick={() => handleImageChange(index)}
                      key={index}
                      className={`${
                        index === currentIndex
                          ? "z-20 rounded-md shadow shadow-black/50"
                          : "z-10"
                      } ${index === 0 ? "rounded-l-md" : ""} ${
                        index === images.length - 1 ? "rounded-r-md" : ""
                      } relative inline-block w-full shrink-0 transform-gpu overflow-hidden focus:outline-none`}
                    >
                      <img
                        alt={`Thumbnail ${index + 1}`}
                        width={180}
                        height={120}
                        className={`${
                          index === currentIndex
                            ? "brightness-110 hover:brightness-110"
                            : "brightness-50 contrast-125 hover:brightness-75"
                        } h-full transform object-cover transition`}
                        src={image || ""}
                      />
                    </motion.button>
                  ))}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
      </MotionConfig>
    </Dialog>
  );
};

export default ImageCarouselModal;
